import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from 'component/Layout/Form/Checkbox';
import { buildViewLabel } from 'component/Offer/Service/__hooks/useViewLabel';

import classNames from 'classnames';

import { useViewTypeListAtom } from 'hooks/form/filter';
import { useAccommodationMeta } from 'hooks/form/meta';
import { useChangedFilterList } from 'hooks/form/useChangedFilterList';
import { QaIdentifier } from 'hooks/useQa';

import { toggleValueInSet } from 'utility/Toggle/toggleValueInSet';

import { ViewType as ViewTypeEnum } from 'proto/Offer/Accommodation/ViewType';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import {
    MetaValue,
    useDisabledStatus,
} from '../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../Segment/FilterLabel';

interface ViewTypeProps {
    viewTypeListArray: ViewTypeEnum[];
    disableWhen?: MetaValue;
}

const ViewType = ({
    viewTypeListArray,
    disableWhen = MetaValue.NoResult,
}: ViewTypeProps) => {
    const { t } = useTranslation();
    const [accommodationMeta] = useAccommodationMeta();
    const [viewTypeList, setViewTypeList] = useViewTypeListAtom();
    const { onToggle, onClear, isFilterChanged } = useChangedFilterList();
    const getDisabledStatus = useDisabledStatus(disableWhen);

    const onSelect = useCallback(
        (selection: ViewTypeEnum) => {
            onToggle(selection);
            setViewTypeList((current) => toggleValueInSet(current, selection));
        },
        [setViewTypeList, onToggle]
    );

    return (
        <Segment
            headline={{ text: t('TRANS-view') }}
            withDeleteLink
            qaIdentifier={QaIdentifier.Search_Filter_Section_ViewType}
            onDelete={() => {
                onClear(() => [...viewTypeList]);
                setViewTypeList(new Set());
            }}
            filterList={viewTypeListArray.map((viewType) => {
                const viewTypeMeta = accommodationMeta?.viewType?.[viewType];
                const label = buildViewLabel(viewType, t);
                const isChecked = viewTypeList.has(viewType);
                return {
                    content: (
                        <Checkbox
                            key={viewType}
                            input={{
                                id: `cbViewType${viewType}`,
                                value: viewType,
                                name: `cbViewType${viewType}`,
                            }}
                            onChange={() => onSelect(viewType)}
                            isChecked={isChecked}
                            customClass={{
                                Wrapper: classNames({
                                    [styles.Changed]: isFilterChanged(viewType),
                                }),
                            }}
                            withUXSelection
                            isDisabled={
                                !isChecked &&
                                viewTypeMeta &&
                                getDisabledStatus(viewTypeMeta)
                            }
                        >
                            <FilterLabel labelText={label} {...viewTypeMeta} />
                        </Checkbox>
                    ),
                    isSelected: isChecked,
                    filterValue: label,
                };
            })}
        />
    );
};

export default ViewType;
